import React, { useState, useEffect } from "react";
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar";
import Swal from "sweetalert2";
import { ShieldCheck, User, Save, X, Loader2, CheckSquare, Square } from "lucide-react";

const PermissionManager = () => {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [saving, setSaving] = useState(false);

  // Define all available screens in your system
  const allScreens = [
    { id: "DASHBOARD", label: "Dashboard" },
    { id: "PROJECTS", label: "Projects" },
    { id: "PLOTS", label: "Plots" },
    { id: "MEMBERS", label: "Members" },
    { id: "PAYMENTS", label: "Payments" },
    { id: "HOLIDAYS", label: "Holidays" },
    { id: "DOCUMENTS", label: "Documents" },
    { id: "BRANCHES", label: "Branches" },
    { id: "DESIGNATIONS", label: "Designations" },
    { id: "LEADS", label: "Leads" },
    { id: "STAFF", label: "Staff" }
  ];

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.PERMISSION_GET_STAFF}`);
      const data = await res.json();
      setStaffList(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchStaff(); }, []);

  const handleManageClick = async (staff) => {
    setSelectedStaff(staff);
    try {
      const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.PERMISSION_GET_BY_STAFF}/${staff.staffId}`);
      const currentPerms = await res.json();
      setSelectedPermissions(currentPerms);
    } catch (e) { console.error(e); }
  };

  const togglePermission = (id) => {
    setSelectedPermissions(prev => 
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.PERMISSION_UPDATE}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          staffId: selectedStaff.staffId,
          screenNames: selectedPermissions
        })
      });
      if (res.ok) {
        Swal.fire("Success", "Access permissions updated!", "success");
        setSelectedStaff(null);
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const styles = {
    container: { padding: "40px 60px", backgroundColor: "#f4f7fe", minHeight: "calc(100vh - 70px)", fontFamily: "'Inter', sans-serif" },
    card: { backgroundColor: "#fff", borderRadius: "24px", padding: "30px", boxShadow: "0 10px 30px rgba(0,0,0,0.02)" },
    staffRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "15px 20px", borderBottom: "1px solid #f1f5f9", transition: "0.2s" },
    modalOverlay: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, backdropFilter: "blur(4px)" },
    modalContent: { backgroundColor: "#fff", borderRadius: "28px", width: "500px", padding: "40px" },
    permItem: { display: "flex", alignItems: "center", gap: "12px", padding: "12px", borderRadius: "12px", cursor: "pointer", transition: "0.2s" }
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        <div style={{ marginBottom: "35px" }}>
          <h1 style={{ fontSize: "30px", fontWeight: "900", color: "#1b2559", margin: 0 }}>Login Users</h1>
          <p style={{ color: "#a3aed0", marginTop: "5px" }}>Manage which screens staff members can access.</p>
        </div>

        <div style={styles.card}>
          {loading ? <Loader2 className="animate-spin" /> : staffList.map(s => (
            <div key={s.staffId} style={styles.staffRow}>
              <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                <div style={{ width: "40px", height: "40px", backgroundColor: "#eef2ff", borderRadius: "10px", display: "flex", justifyContent: "center", alignItems: "center", color: "#4f46e5" }}><User size={20} /></div>
                <div>
                  <div style={{ fontWeight: "800", color: "#1b2559" }}>{s.fullName}</div>
                  <div style={{ fontSize: "12px", color: "#a3aed0" }}>{s.phoneNumber}</div>
                </div>
              </div>
              <button onClick={() => handleManageClick(s)} style={{ backgroundColor: "#4f46e5", color: "#fff", border: "none", padding: "10px 20px", borderRadius: "10px", fontWeight: "700", cursor: "pointer" }}>Manage Screens</button>
            </div>
          ))}
        </div>

        {selectedStaff && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "30px" }}>
                <h3 style={{ margin: 0, fontWeight: "900" }}>Permissions: {selectedStaff.fullName}</h3>
                <X style={{ cursor: "pointer", color: "#a3aed0" }} onClick={() => setSelectedStaff(null)} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "30px" }}>
                {allScreens.map(screen => (
                  <div key={screen.id} onClick={() => togglePermission(screen.id)} 
                       style={{ ...styles.permItem, backgroundColor: selectedPermissions.includes(screen.id) ? "#f0fdf4" : "#f8fafc", border: selectedPermissions.includes(screen.id) ? "1.5px solid #10b981" : "1.5px solid #e2e8f0" }}>
                    {selectedPermissions.includes(screen.id) ? <CheckSquare size={18} color="#10b981" /> : <Square size={18} color="#cbd5e1" />}
                    <span style={{ fontSize: "14px", fontWeight: "700", color: "#1b2559" }}>{screen.label}</span>
                  </div>
                ))}
              </div>
              <button onClick={handleSave} disabled={saving} style={{ width: "100%", backgroundColor: "#1b2559", color: "#fff", border: "none", padding: "15px", borderRadius: "12px", fontWeight: "700", cursor: "pointer", display: "flex", justifyContent: "center", gap: "10px" }}>
                {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />} Save Permissions
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PermissionManager;